import React from 'react';
import { useTranslation } from 'react-i18next';

export const RoadmapPage: React.FC = () => {
  const { t } = useTranslation();

  const roadmapPhases = [
    {
      period: t('pages.roadmap.phases.phase1.date'),
      title: t('pages.roadmap.phases.phase1.title'),
      subtitle: t('pages.roadmap.phases.phase1.focus'), // 'focus' in JSON maps to subtitle usage
      items: Object.values(t('pages.roadmap.phases.phase1.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase1.goal'),
      status: 'completed' // Keeping logic-based status or mapping it if needed. JSON has "status": "Selesai"/"Completed" text, not key. But for styling we need key. Can derive or keep hardcoded if phrases are static.
    },
    {
      period: t('pages.roadmap.phases.phase2.date'),
      title: t('pages.roadmap.phases.phase2.title'),
      subtitle: t('pages.roadmap.phases.phase2.focus'),
      items: Object.values(t('pages.roadmap.phases.phase2.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase2.goal'),
      status: 'active'
    },
    {
      period: t('pages.roadmap.phases.phase3.date'),
      title: t('pages.roadmap.phases.phase3.title'),
      subtitle: t('pages.roadmap.phases.phase3.focus'),
      items: Object.values(t('pages.roadmap.phases.phase3.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase3.goal'),
      status: 'upcoming'
    },
    {
      period: t('pages.roadmap.phases.phase4.date'),
      title: t('pages.roadmap.phases.phase4.title'),
      subtitle: t('pages.roadmap.phases.phase4.focus'),
      items: Object.values(t('pages.roadmap.phases.phase4.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase4.goal'),
      status: 'upcoming'
    },
    {
      period: t('pages.roadmap.phases.phase5.date'),
      title: t('pages.roadmap.phases.phase5.title'),
      subtitle: t('pages.roadmap.phases.phase5.focus'),
      items: Object.values(t('pages.roadmap.phases.phase5.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase5.goal'),
      status: 'upcoming'
    },
    {
      period: t('pages.roadmap.phases.phase6.date'),
      title: t('pages.roadmap.phases.phase6.title'),
      subtitle: t('pages.roadmap.phases.phase6.focus'),
      items: Object.values(t('pages.roadmap.phases.phase6.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase6.goal'),
      status: 'upcoming'
    },
    {
      period: t('pages.roadmap.phases.phase7.date'),
      title: t('pages.roadmap.phases.phase7.title'),
      subtitle: t('pages.roadmap.phases.phase7.focus'),
      items: Object.values(t('pages.roadmap.phases.phase7.items', { returnObjects: true }) || {}),
      goal: t('pages.roadmap.phases.phase7.goal'),
      status: 'upcoming'
    }
  ];

  return (
    <div className="min-h-screen relative py-20 overflow-hidden">
      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-900/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="hero-badge inline-block px-4 py-2 bg-gold/10 rounded-full mb-6">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">{t('pages.roadmap.badge')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-6">
            {t('pages.roadmap.title')}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {t('pages.roadmap.description')}
          </p>
        </div>

        <div className="max-w-5xl mx-auto relative">
          {/* Vertical Line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gold via-gold/50 to-transparent transform -translate-x-1/2 md:translate-x-0 hidden md:block"></div>

          <div className="space-y-12">
            {roadmapPhases.map((phase, index) => (
              <div
                key={index}
                className={`relative flex flex-col md:flex-row gap-8 items-center ${index % 2 === 0 ? 'md:flex-row-reverse' : ''
                  }`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full border-4 border-bg-primary bg-gold shadow-[0_0_15px_rgba(212,175,55,0.5)] transform -translate-x-1/2 md:translate-x-1/2 flex items-center justify-center z-10 hidden md:flex">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                </div>

                {/* Content Card */}
                <div className="w-full md:w-1/2 pl-0 md:px-12">
                  <div
                    className="group relative bg-surface/50 backdrop-blur-sm border border-gold/10 hover:border-gold/30 rounded-2xl p-6 transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.1)] hover:-translate-y-1"
                  >
                    {/* Glowing effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>

                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4 border-b border-gray-800 pb-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${phase.status === 'completed' ? 'bg-green-900/30 text-green-400 border border-green-900/50' :
                          phase.status === 'active' ? 'bg-gold/20 text-gold border border-gold/30 animate-pulse' :
                            'bg-gray-800 text-gray-400'
                          }`}>
                          {phase.status === 'active' ? 'Current Phase' : phase.period}
                        </span>
                        {phase.status === 'completed' && <i className="fas fa-check-circle text-green-400"></i>}
                      </div>

                      <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-gold transition-colors">
                        {phase.title}
                      </h3>
                      <p className="text-gold/80 font-medium mb-4 text-sm">{phase.subtitle}</p>

                      <ul className="space-y-3 mb-6">
                        {phase.items.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-gray-300 text-sm">
                            <i className="fas fa-caret-right text-gold mt-1"></i>
                            <span>{item as string}</span>
                          </li>
                        ))}
                      </ul>

                      {index !== 6 && (
                        <div className="relative group/goal overflow-hidden rounded-xl bg-gradient-to-br from-bg-primary/80 to-surface/80 p-[1px]">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gold/20 to-transparent translate-x-[-100%] group-hover/goal:translate-x-[100%] transition-transform duration-1000"></div>
                          <div className="relative h-full bg-bg-primary/90 backdrop-blur-sm rounded-xl p-4 border border-gold/10 group-hover/goal:border-gold/30 transition-colors">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center">
                                <i className="fas fa-flag text-gold text-xs"></i>
                              </div>
                              <span className="text-xs font-bold text-gold uppercase tracking-wider">Goal</span>
                            </div>
                            <p className="text-sm text-gray-200 italic leading-relaxed">"{phase.goal}"</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Empty side for layout balance */}
                <div className="w-full md:w-1/2"></div>
              </div>
            ))}
          </div>

          {/* Phase 7 Ultimate Goal - Removed from card, displayed prominently */}
          <div className="mt-32 mb-16 relative z-10 text-center px-4">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gold/5 blur-[100px] rounded-full pointer-events-none"></div>

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-8 backdrop-blur-sm">
                <i className="fas fa-flag text-gold animate-bounce"></i>
                <span className="text-gold font-bold uppercase tracking-widest text-sm">Ultimate Vision</span>
              </div>

              <h2 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-gold to-white mb-10 drop-shadow-[0_0_25px_rgba(212,175,55,0.2)] max-w-5xl mx-auto leading-tight">
                "{roadmapPhases[6].goal}"
              </h2>

              <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto rounded-full opacity-80"></div>
            </div>
          </div>

          {/* Final Statement */}
          <div className="mt-20 text-center relative z-10">
            <div className="inline-block p-8 bg-gradient-to-br from-surface to-bg-primary rounded-2xl border border-gold/20 max-w-2xl mx-auto shadow-2xl">
              <i className="fas fa-quote-left text-3xl text-gold/30 mb-4 block"></i>
              <p className="text-xl text-white font-medium italic mb-4">
                "{t('pages.roadmap.footer_quote')}"
              </p>
              <div className="w-20 h-1 bg-gradient-to-r from-transparent via-gold to-transparent mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};